import React from "react";
import { Box, Flex } from "theme-ui";

import H5 from "components/typography/H5";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { mnt, mntCard, mntPill } from "theme/maintenanceTokens";

/**
 * MASTER_PLAN.md §3.9 #22 — right-rail plan config summary (mockup 13).
 * Read-only snapshot of the plan's four config surfaces:
 *   - Pricing        (contract rates / discount / payment terms)
 *   - Cadence        (per-service-type expected schedule)
 *   - Notifications  (event x recipient matrix; summarized as counts)
 *   - Payment        (provider payment terms, days + mode)
 *
 * The "Edit plan" button lifts up to the parent so the modal
 * lives at PlanDetail scope alongside other plan-level actions.
 *
 * Every subsection reads defensively from `pricingConfig`,
 * `serviceTypesConfig`, `notificationConfig`. All three are nullable
 * per §3.3 — treat absent as "using defaults", never crash.
 */
export type ServicePlanForConfigPanel = {
  intakeStyle?: "work_order_fed" | "quote_fed";
  /** KNOWN_ISSUES item 18 - whether the client can raise work from the
   * portal on this plan. Shown here because it is a capability we grant
   * and then forget we granted; an operator reading the config should be
   * able to see it without opening the edit modal. */
  clientSelfServiceEnabled?: boolean;
  serviceTypesConfig?: Array<{
    type: string;
    cadence: "annual" | "biennial" | "ad_hoc" | "none";
    defaultOnNewWo: boolean;
    pricingOverride?: Record<string, any>;
    cadenceRule?: { mode?: string } | null;
  }> | null;
  pricingConfig?: Record<string, any> | null;
  notificationConfig?: Record<string, boolean> | null;
};

export default function PlanConfigPanel({
  plan,
  onEdit,
  canEdit,
  products,
  layout = "stacked",
}: {
  plan: ServicePlanForConfigPanel;
  onEdit: () => void;
  canEdit: boolean;
  /** Product list so a cadence row's `type` (which holds a PRODUCT ID,
   * written by the EditPlanModal service-type picker) resolves to the
   * product NAME. Without it, an unresolved id would render raw — the
   * cadence-UUID bug (LIVE_VS_MOCKUP_GAP P5). Optional: when absent the
   * legacy-key map is tried and any leftover UUID is masked, never shown. */
  products?: Array<{ id: string; name: string }> | null;
  /** Layout mode. "stacked" (default) is the original vertical card with
   * dividers between sections, used in the narrow rail. "horizontal" lays
   * the sections out as a responsive grid so the card is short and wide,
   * used when the panel sits full-width above the batches (mockup: Jack
   * Session 60). Dividers are dropped in horizontal mode; grid gaps
   * separate the sections instead. */
  layout?: "stacked" | "horizontal";
}) {
  const horizontal = layout === "horizontal";
  const pricing = plan?.pricingConfig || {};
  const serviceTypes = plan?.serviceTypesConfig || [];
  const notifs = plan?.notificationConfig || {};

  // ─── Intake style summary ────────────────────────────────────
  // The switch that decides which surfaces the plan shows. Rendered
  // first so the plan's shape reads at a glance. Defaults to quote-fed
  // for older plans that predate the column.
  const intakeSummary =
    plan?.intakeStyle === "work_order_fed" ? "Work-order-fed" : "Quote-fed";
  const intakeSubline =
    plan?.intakeStyle === "work_order_fed"
      ? "Client sends work orders"
      : "You issue quotes directly";

  // ─── Pricing summary ─────────────────────────────────────────
  const pricingSummary = (() => {
    if (pricing.discountPct) return `${pricing.discountPct}% off standard`;
    if (pricing.serviceOverrides && Object.keys(pricing.serviceOverrides).length) {
      return "Custom rates per service";
    }
    return "Standard rates";
  })();

  const paymentTermsSummary = (() => {
    if (pricing.paymentTerms) return formatPaymentTerms(String(pricing.paymentTerms));
    if (pricing.providerPaymentTerms?.days) {
      return `Net-${pricing.providerPaymentTerms.days}`;
    }
    return "Default terms";
  })();

  // ─── Cadence summary ─────────────────────────────────────────
  // If the plan has no serviceTypesConfig at all, show a hint that it
  // hasn't been configured yet. Otherwise render one line per service,
  // with a "Recurring Work" marker when the row carries a live
  // cadenceRule (the scheduler will auto-generate WOs for it).
  const cadenceLines = serviceTypes.length
    ? serviceTypes.map((s) => ({
        type: resolveServiceTypeName(s.type, products),
        cadence: humanCadence(s.cadence),
        recurring: !!(s.cadenceRule && s.cadenceRule.mode),
      }))
    : [];

  const recurringCount = cadenceLines.filter((c) => c.recurring).length;

  // ─── Notification summary ────────────────────────────────────
  // Count enabled cells rather than trying to describe the whole
  // matrix. "N of M events sending" is honest and fits the right rail
  // without a wall of text.
  const notifSummary = (() => {
    const cfg = notifs || {};
    const activeKeys = Object.entries(cfg)
      .filter(([, v]) => v)
      .map(([k]) => k)
      .sort();
    const sig = activeKeys.join("|");
    if (activeKeys.length === 0) {
      if (Object.keys(cfg).length === 0) return "Not configured";
      return "Corrigo-managed";
    }
    const selfServiceKeys = [
      "wo-received.plan-primary-contact",
      "quote-sent.plan-primary-contact",
      "quote-accepted.plan-primary-contact",
      "project-scheduled.plan-primary-contact",
      "project-in-flight.plan-primary-contact",
      "project-completed.plan-primary-contact",
      "invoice-sent.plan-primary-contact",
      "invoice-paid.plan-primary-contact",
      "weekly-plan-digest.plan-primary-contact",
    ].sort().join("|");
    if (sig === selfServiceKeys) return "Self-service";
    const mixedKeys = [
      "project-completed.plan-primary-contact",
      "invoice-sent.plan-primary-contact",
      "invoice-paid.plan-primary-contact",
      "weekly-plan-digest.plan-primary-contact",
    ].sort().join("|");
    if (sig === mixedKeys) return "Mixed";
    return `Custom (${activeKeys.length} enabled)`;
  })();

  const notifSubline: string | undefined = (() => {
    if (notifSummary === "Corrigo-managed") return "All silent";
    if (notifSummary === "Self-service") return "Every event → plan contact";
    if (notifSummary === "Mixed") return "Milestones → plan contact";
    if (notifSummary === "Not configured") return "Using defaults";
    return undefined;
  })();

  // ─── Provider payment ────────────────────────────────────────
  const providerPaymentSummary = (() => {
    const t = pricing.providerPaymentTerms;
    if (!t) return "Default";
    const days = t.days ? `Net-${t.days}` : "";
    const mode =
      t.mode === "on_client_payment"
        ? "on client payment"
        : t.mode === "hard_net"
          ? "hard net"
          : "";
    return [days, mode].filter(Boolean).join(" · ") || "Default";
  })();

  return (
    <Box>
      <Flex sx={{ alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
        <H5 fontFamily="Poppins" fontWeight={600} fontSize={"14px"} margin={0}>
          Plan config
        </H5>
        {canEdit && (
          <Box
            as="button"
            onClick={onEdit}
            sx={{
              background: "transparent",
              border: "none",
              padding: 0,
              fontSize: "12px",
              fontFamily: "Poppins",
              color: mnt.link,
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Edit
          </Box>
        )}
      </Flex>

      <Box
        sx={{
          ...mntCard,
          padding: "16px 18px",
          ...(horizontal
            ? {
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "16px 28px",
                alignItems: "start",
              }
            : {}),
        }}
      >
        <ConfigRow label="Intake" value={intakeSummary} sub={intakeSubline} />
        <ConfigRow
          label="Client self-service"
          value={plan?.clientSelfServiceEnabled ? "On" : "Off"}
          sub={
            plan?.clientSelfServiceEnabled
              ? "Client can request work from the portal"
              : "Portal is read-only for this client"
          }
        />

        {!horizontal && <Divider />}

        <ConfigRow label="Pricing" value={pricingSummary} sub={paymentTermsSummary} />

        {!horizontal && <Divider />}

        <Box>
          <Subtext
            fontSize={"11px"}
            fontFamily={"Poppins"}
            fontWeight={600}
            margin={0}
            color={mnt.textMuted}
          >
            CADENCE{recurringCount > 0 ? ` · ${recurringCount} recurring` : ""}
          </Subtext>
          {cadenceLines.length ? (
            cadenceLines.map((c) => (
              <Box key={c.type} sx={{ marginTop: "6px" }}>
                <Span fontFamily={"Poppins"} fontSize={"12px"} sx={{ display: "block" }}>
                  {c.type}
                </Span>
                <Flex sx={{ alignItems: "center", gap: "6px", marginTop: "2px" }}>
                  <Span fontFamily={"Poppins"} fontSize={"11px"} color={mnt.textMuted}>
                    {c.cadence}
                  </Span>
                  {c.recurring && (
                    <Box sx={{ ...mntPill("current"), fontSize: "10px", padding: "1px 8px" }}>
                      Recurring
                    </Box>
                  )}
                </Flex>
              </Box>
            ))
          ) : (
            <Span
              fontFamily={"Poppins"}
              fontSize={"12px"}
              color={mnt.textMuted}
              sx={{ display: "block", marginTop: "6px" }}
            >
              Not configured
            </Span>
          )}
        </Box>

        {!horizontal && <Divider />}

        <ConfigRow label="Notifications" value={notifSummary} sub={notifSubline} />

        {!horizontal && <Divider />}

        <ConfigRow label="Provider payment" value={providerPaymentSummary} />

        {pricing.providerTermsDisclosure && (
          <>
            {!horizontal && <Divider />}
            <Subtext
              fontSize={"11px"}
              fontFamily={"Poppins"}
              fontWeight={600}
              margin={0}
              color={mnt.textMuted}
            >
              TERMS DISCLOSURE
            </Subtext>
            <Subtext
              fontSize={"11px"}
              fontFamily={"Poppins"}
              margin={0}
              marginTop={"6px"}
              sx={{
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {String(pricing.providerTermsDisclosure).slice(0, 200)}
              {String(pricing.providerTermsDisclosure).length > 200 ? "…" : ""}
            </Subtext>
          </>
        )}
      </Box>
    </Box>
  );
}

function ConfigRow({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <Box>
      <Subtext
        fontSize={"11px"}
        fontFamily={"Poppins"}
        fontWeight={600}
        margin={0}
        color={mnt.textMuted}
      >
        {label.toUpperCase()}
      </Subtext>
      <Span
        fontFamily={"Poppins"}
        fontSize={"12px"}
        sx={{ display: "block", marginTop: "4px" }}
      >
        {value}
      </Span>
      {sub && (
        <Subtext
          fontSize={"11px"}
          fontFamily={"Poppins"}
          margin={0}
          marginTop={"2px"}
          color={mnt.textMuted}
        >
          {sub}
        </Subtext>
      )}
    </Box>
  );
}

function Divider() {
  return (
    <Box
      sx={{
        height: "0.5px",
        background: mnt.divider,
        marginY: "12px",
      }}
    />
  );
}

function humanServiceType(t: string): string {
  if (!t) return "";
  const map: Record<string, string> = {
    mulched: "Mulch",
    dig_out: "Dig-out",
    inspection: "Inspection",
    note: "Note",
  };
  return map[t] || t.replace(/_/g, " ");
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Resolve a cadence row's `type` to a display name. The value is a
 * PRODUCT ID (EditPlanModal's service-type picker stores `product.id`),
 * so:
 *   1) look it up in the products list -> product name;
 *   2) else try the legacy service-type key map (mulched/dig_out/...);
 *   3) else, if it's a bare UUID we couldn't resolve, show "Service"
 *      rather than leak a raw id onto the surface (P5 guard).
 * This never renders a UUID.
 */
function resolveServiceTypeName(
  type: string,
  products?: Array<{ id: string; name: string }> | null,
): string {
  if (!type) return "";
  const match = (products || []).find((p) => p.id === type);
  if (match?.name) return match.name;
  const legacy = humanServiceType(type);
  if (UUID_RE.test(type)) return "Service"; // unresolved id — never show raw
  return legacy;
}

function humanCadence(c: string): string {
  const map: Record<string, string> = {
    annual: "Annual",
    biennial: "Biennial",
    ad_hoc: "Ad-hoc",
    none: "Off",
  };
  return map[c] || c;
}

// Normalize a stored client payment-terms value for display so the
// Pricing row reads consistently with the Provider payment row (which
// renders "Net-65"). Handles net terms in any casing/separator
// ("net60", "net_60", "NET 60" -> "Net-60") and title-cases other known
// terms ("prepay", "deposit", "due_on_receipt"). Unknown values fall back
// to a spaced, title-cased form rather than raw lowercase.
function formatPaymentTerms(raw: string): string {
  const v = raw.trim();
  const net = v.match(/^net[\s_-]*(\d+)$/i);
  if (net) return `Net-${net[1]}`;
  const words = v
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : w));
  return words.join(" ");
}
